import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Shield, Lock, FileText, Download, Loader2, ArrowLeft, KeyRound, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/**
 * /locker/share/:shareId — public read-only view for a nominee/family member.
 * No account required. Passphrase-gated when the owner sets one.
 */
export default function LockerShare() {
  const { shareId } = useParams();
  const [meta, setMeta]   = useState(null);
  const [err, setErr]     = useState(null);
  const [passphrase, setPassphrase] = useState("");
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  useSeo({ title: "Shared documents · find2near",
           description: "Access documents shared securely by a family member." });

  useEffect(() => {
    api.get(`/locker/share/${shareId}`).then(r => setMeta(r.data))
       .catch(e => setErr(e?.response?.data?.detail || "Share unavailable"));
  }, [shareId]);

  const open = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/locker/share/${shareId}/open`,
                               passphrase ? { passphrase } : {});
      setData(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 423) toast.error("Passphrase required");
      else if (e?.response?.status === 403) toast.error("Wrong passphrase");
      else toast.error(d || "Couldn't open share");
    } finally { setBusy(false); }
  };

  const download = (att) => {
    try {
      const bin = atob(att.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: att.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = att.name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Download failed"); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-950 text-white grid place-items-center p-6">
      <div className="rounded-3xl bg-white/5 border border-white/10 p-6 text-center max-w-sm" data-testid="locker-share-error">
        <Shield size={36} className="mx-auto text-rose-400 mb-2"/>
        <div className="text-lg font-black">Link unavailable</div>
        <div className="text-[12px] text-white/60 mt-1">{err}</div>
      </div>
    </div>
  );
  if (!meta) return <div className="min-h-screen bg-slate-950 text-white grid place-items-center"><Loader2 className="animate-spin"/></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-black text-white" data-testid="locker-share-page">
      <div className="bg-gradient-to-br from-amber-600 to-rose-700 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Link to="/" className="text-[11px] text-white/70 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={12}/> Home</Link>
          <div className="text-[11px] uppercase tracking-widest font-black opacity-80 flex items-center gap-1 mt-2"><Shield size={12}/> Shared with you</div>
          <h1 className="heading text-2xl md:text-4xl font-black mt-1">{meta.owner_name} shared {meta.count} document{meta.count !== 1 ? "s" : ""}</h1>
          {meta.nominee_name && <p className="text-sm text-white/80 mt-1">For: <span className="font-black">{meta.nominee_name}</span></p>}
          <p className="text-[11px] text-white/70 mt-1">Expires {new Date(meta.expires_at).toLocaleString()}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {!data ? (
          <div className="rounded-3xl bg-white/5 border border-white/10 p-4 space-y-3" data-testid="locker-share-gate">
            {meta.requires_passphrase && (
              <div>
                <label className="text-[10px] uppercase font-black text-white/60 inline-flex items-center gap-1"><KeyRound size={11}/> Passphrase required</label>
                <Input type="password" value={passphrase} onChange={e => setPassphrase(e.target.value)}
                       placeholder="Enter passphrase" className="rounded-full bg-white/5 border-white/10 text-white mt-1" data-testid="locker-share-passphrase"/>
              </div>
            )}
            <Button disabled={busy || (meta.requires_passphrase && !passphrase)} onClick={open}
                    className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] w-full" data-testid="locker-share-open-btn">
              {busy ? <Loader2 size={12} className="animate-spin"/> : <><Lock size={12} className="mr-1"/> View documents</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {data.documents.map(d => (
              <div key={d.doc_id} className="rounded-3xl bg-white/5 border border-white/10 p-4 space-y-2" data-testid={`shared-doc-${d.doc_id}`}>
                <div className="text-[10px] uppercase text-emerald-300 font-black">{d.group} · {d.category}</div>
                <div className="font-black text-lg">{d.title}</div>
                {d.expires_on && <div className="text-[11px] text-amber-300 font-black">Expires {new Date(d.expires_on).toLocaleDateString()}</div>}
                {d.metadata && Object.keys(d.metadata).length > 0 && (
                  <div className="rounded-2xl bg-white/5 p-2 space-y-1">
                    {Object.entries(d.metadata).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2 text-[11px]">
                        <span className="text-white/50">{k.replace(/_/g, " ")}</span>
                        <span className="text-white font-black text-right break-all">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {d.attachments?.length > 0 && (
                  <div className="space-y-2">
                    {d.attachments.map((a, i) => <SharedAttachment key={i} a={a} d={d} i={i} download={download}/>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SharedAttachment({ a, d, i, download }) {
  const [url, setUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const isImg = (a.content_type || "").startsWith("image/");
  const isPdf = (a.content_type || "") === "application/pdf" || (a.name || "").toLowerCase().endsWith(".pdf");
  useEffect(() => {
    try {
      const bin = atob(a.base64);
      const bytes = new Uint8Array(bin.length);
      for (let j = 0; j < bin.length; j++) bytes[j] = bin.charCodeAt(j);
      const u = URL.createObjectURL(new Blob([bytes], { type: a.content_type || "application/octet-stream" }));
      setUrl(u);
      return () => URL.revokeObjectURL(u);
    } catch { /* skip */ }
  }, [a]);
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10">
      <div className="flex items-center gap-2 px-2 py-1.5">
        <FileText size={12} className="text-emerald-400"/>
        <div className="flex-1 truncate text-[11px]">{a.name}</div>
        {(isImg || isPdf) && url && (
          <Button onClick={() => setOpen(o => !o)} className="rounded-full bg-sky-500 text-white font-black text-[10px] h-7 px-2" data-testid={`shared-preview-${d.doc_id}-${i}`}>
            {open ? <><EyeOff size={11} className="mr-1"/> Hide</> : <><Eye size={11} className="mr-1"/> Preview</>}
          </Button>
        )}
        <Button onClick={() => download(a)} className="rounded-full bg-emerald-500 text-slate-900 font-black text-[10px] h-7 px-2" data-testid={`shared-download-${d.doc_id}-${i}`}>
          <Download size={11} className="mr-1"/> Save
        </Button>
      </div>
      {open && isImg && url && <div className="p-2 bg-black/40"><img src={url} alt={a.name} className="w-full max-h-[60vh] object-contain rounded-xl"/></div>}
      {open && isPdf && url && <iframe src={url} title={a.name} className="w-full h-[70vh] rounded-b-2xl border-0 bg-white" data-testid={`shared-pdf-${d.doc_id}-${i}`}/>}
    </div>
  );
}
